import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Tag, RefreshCw, TrendingDown, Users, Layers, Printer, Loader2,
  FileText, Filter,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { printReport } from "@/lib/printReport";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.message_ar || d.message_en || d.msg || JSON.stringify(d);
  return String(d);
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const kindLabel = (k, isAr) => {
  const m = {
    service: { ar: "خدمة", en: "Service" },
    product: { ar: "منتج", en: "Product" },
    package: { ar: "باقة", en: "Package" },
  }[k] || { ar: k || "—", en: k || "—" };
  return isAr ? m.ar : m.en;
};

export default function DiscountReport({ range, template }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [staff, setStaff] = useState([]);

  // Filters
  const [kind, setKind] = useState("");        // service|product|package|""
  const [reasonId, setReasonId] = useState("");
  const [staffId, setStaffId] = useState("");

  // Load lookups once
  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/discount-reasons").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([r, u]) => {
      if (cancel) return;
      setReasons(Array.isArray(r.data) ? r.data : []);
      setStaff((u.data || []).filter((x) => x.role !== "admin"));
    });
    return () => { cancel = true; };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (range?.start) q.set("start", range.start);
      if (range?.end)   q.set("end", range.end);
      if (kind)         q.set("kind", kind);
      if (reasonId)     q.set("reason_id", reasonId);
      if (staffId)      q.set("staff_id", staffId);
      const r = await api.get(`/reports/discount-analysis?${q}`);
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  // Inline data fetch — depending directly on filter scalars keeps the
  // memoization linter happy and the effect predictable.
  useEffect(() => {
    let cancelled = false;
    const q = new URLSearchParams();
    if (range?.start) q.set("start", range.start);
    if (range?.end)   q.set("end", range.end);
    if (kind)         q.set("kind", kind);
    if (reasonId)     q.set("reason_id", reasonId);
    if (staffId)      q.set("staff_id", staffId);
    setLoading(true);
    api.get(`/reports/discount-analysis?${q}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) toast.error(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range?.start, range?.end, kind, reasonId, staffId]);

  const totals = data || { total_line_discount: 0, total_global_discount: 0, total_combined: 0, invoice_count: 0, discounted_line_count: 0 };
  const reasonRows = data?.by_reason || [];
  const staffRows = data?.by_staff || [];
  const kindRows = data?.by_kind || [];
  const lines = data?.lines || [];

  const reasonTotalForPct = useMemo(() =>
    reasonRows.reduce((s, r) => s + r.amount, 0) || 1, [reasonRows]);

  const print = () => {
    if (!data) return;
    // Translate filter selections into human-readable values for the
    // print header — keeps the printed PDF self-explanatory.
    const kindLabels = { service: { ar: "خدمات فقط", en: "Services only" },
                         product: { ar: "منتجات فقط", en: "Products only" },
                         package: { ar: "باقات فقط", en: "Packages only" } };
    const filterParts = [];
    if (kind)     filterParts.push(isAr ? kindLabels[kind].ar : kindLabels[kind].en);
    if (reasonId) {
      const r = reasons.find((x) => x.reason_id === reasonId);
      if (r) filterParts.push(isAr ? r.label_ar : r.label_en);
    }
    if (staffId) {
      const u = staff.find((x) => x.user_id === staffId);
      if (u) filterParts.push(u.name || u.username);
    }

    printReport({
      title: isAr ? "تقرير الخصومات" : "Discount Report",
      subtitle: isAr ? "تفاصيل الخصومات على البنود مع الأسباب" : "Per-line discount detail with reasons",
      lang,
      template,
      meta: [
        { label: isAr ? "الفترة" : "Period", value: `${range?.start || "—"} → ${range?.end || "—"}` },
        ...(filterParts.length ? [{ label: isAr ? "الفلاتر" : "Filters", value: filterParts.join(" / ") }] : []),
        { label: isAr ? "عدد الفواتير" : "Invoices", value: String(totals.invoice_count || 0) },
        { label: isAr ? "بنود مخصومة" : "Discounted lines", value: String(lines.length) },
      ],
      columns: [
        isAr ? "التاريخ" : "Date",
        isAr ? "الفاتورة" : "Invoice",
        isAr ? "العميل" : "Customer",
        isAr ? "البند" : "Item",
        isAr ? "الفئة" : "Kind",
        isAr ? "السعر" : "Unit",
        isAr ? "الكمية" : "Qty",
        isAr ? "الخصم" : "Discount",
        isAr ? "السبب" : "Reason",
        isAr ? "الموظف" : "Staff",
      ],
      rows: lines.map((row) => [
        row.created_at ? new Date(row.created_at).toLocaleDateString(isAr ? "ar-EG" : "en-GB", { year: "numeric", month: "short", day: "2-digit" }) : "—",
        row.invoice_number || "—",
        row.customer_name || "—",
        row.item_name || "—",
        kindLabel(row.kind, isAr),
        { value: fmt(row.unit_price), className: "num" },
        { value: String(row.qty || 0), className: "num" },
        { value: `-${fmt(row.line_discount_amount)}`, className: "num neg" },
        row.discount_reason_label || "—",
        row.employee_name || "—",
      ]),
      totals: [
        { label: isAr ? "إجمالي خصومات البنود" : "Line Discounts",   value: fmt(totals.total_line_discount), className: "rose" },
        { label: isAr ? "خصم الفاتورة الإجمالي" : "Global Discounts", value: fmt(totals.total_global_discount), className: "muted" },
        { label: isAr ? "المجموع الكلي" : "Combined Total",          value: fmt(totals.total_combined), className: "gold" },
      ],
    });
  };

  return (
    <div className="space-y-4" data-testid="discount-report">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={TrendingDown} color="rose"
          label={isAr ? "إجمالي خصومات البنود" : "Line Discounts"}
          value={fmt(totals.total_line_discount)} suffix={isAr ? "ر.ق" : "QAR"}/>
        <KpiCard icon={Tag} color="amber"
          label={isAr ? "خصم الفاتورة الإجمالي" : "Global Discounts"}
          value={fmt(totals.total_global_discount)} suffix={isAr ? "ر.ق" : "QAR"}/>
        <KpiCard icon={TrendingDown} color="purple"
          label={isAr ? "المجموع الكلي" : "Combined Total"}
          value={fmt(totals.total_combined)} suffix={isAr ? "ر.ق" : "QAR"} highlight/>
        <KpiCard icon={FileText} color="blue"
          label={isAr ? "عدد الفواتير" : "Invoices"}
          value={totals.invoice_count}/>
        <KpiCard icon={Layers} color="emerald"
          label={isAr ? "بنود مخصومة" : "Discounted Lines"}
          value={totals.discounted_line_count || lines.length}/>
      </div>

      {/* Filters bar */}
      <div className="card-surface p-4 flex flex-wrap items-center gap-3" data-testid="discount-filters">
        <Filter size={15} className="text-[var(--gold)]"/>
        <span className="text-sm text-[var(--text-muted)]">{isAr ? "تصفية:" : "Filter:"}</span>

        <select value={kind} onChange={(e) => setKind(e.target.value)}
                data-testid="filter-kind"
                className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{isAr ? "كل الفئات" : "All categories"}</option>
          <option value="service">{isAr ? "خدمات فقط" : "Services only"}</option>
          <option value="product">{isAr ? "منتجات فقط" : "Products only"}</option>
          <option value="package">{isAr ? "باقات فقط" : "Packages only"}</option>
        </select>

        <select value={reasonId} onChange={(e) => setReasonId(e.target.value)}
                data-testid="filter-reason"
                className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{isAr ? "كل الأسباب" : "All reasons"}</option>
          {reasons.map((r) => (
            <option key={r.reason_id} value={r.reason_id}>{isAr ? r.label_ar : r.label_en}</option>
          ))}
        </select>

        <select value={staffId} onChange={(e) => setStaffId(e.target.value)}
                data-testid="filter-staff"
                className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{isAr ? "كل الموظفين" : "All staff"}</option>
          {staff.map((u) => (
            <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>
          ))}
        </select>

        {(kind || reasonId || staffId) && (
          <button onClick={() => { setKind(""); setReasonId(""); setStaffId(""); }}
                  data-testid="filter-reset"
                  className="px-2 py-1 rounded text-xs text-stone-500 hover:bg-stone-100">
            {isAr ? "إعادة" : "Reset"}
          </button>
        )}

        <div className="ms-auto flex items-center gap-2">
          {loading && <Loader2 className="animate-spin text-amber-600" size={14}/>}
          <button onClick={load} disabled={loading}
                  data-testid="discount-refresh"
                  className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 text-xs flex items-center gap-1">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""}/>
            {isAr ? "تحديث" : "Refresh"}
          </button>
          <button onClick={print}
                  data-testid="discount-print"
                  className="btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1">
            <Printer size={13}/>{isAr ? "طباعة" : "Print"}
          </button>
        </div>
      </div>

      {/* Breakdown grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownPanel
          title={isAr ? "حسب السبب" : "By Reason"}
          icon={Tag}
          rows={reasonRows.map((r) => ({
            key: r.reason_id,
            label: r.label,
            sublabel: `${r.count} ${isAr ? "بند" : "lines"}`,
            value: r.amount,
            pct: (r.amount / reasonTotalForPct) * 100,
          }))}
          empty={isAr ? "لا توجد خصومات حسب السبب." : "No discounts by reason."}
          color="rose"
          isAr={isAr}
        />
        <BreakdownPanel
          title={isAr ? "حسب الموظف" : "By Staff"}
          icon={Users}
          rows={staffRows.map((r) => ({
            key: r.user_id,
            label: r.name || (r.user_id === "—" ? (isAr ? "بدون موظف" : "Unassigned") : r.user_id),
            sublabel: `${r.count} ${isAr ? "بند" : "lines"}`,
            value: r.amount,
            pct: (r.amount / reasonTotalForPct) * 100,
          }))}
          empty={isAr ? "لا توجد خصومات حسب الموظف." : "No discounts by staff."}
          color="amber"
          isAr={isAr}
        />
        <BreakdownPanel
          title={isAr ? "حسب الفئة" : "By Category"}
          icon={Layers}
          rows={kindRows.map((r) => ({
            key: r.kind,
            label: kindLabel(r.kind, isAr),
            sublabel: `${r.count} ${isAr ? "بند" : "lines"}`,
            value: r.amount,
            pct: (r.amount / reasonTotalForPct) * 100,
          }))}
          empty={isAr ? "لا توجد خصومات حسب الفئة." : "No discounts by category."}
          color="purple"
          isAr={isAr}
        />
      </div>

      {/* Drill-down table */}
      <div className="card-surface p-0 overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b border-stone-200">
          <div>
            <h4 className="font-semibold text-[var(--text)]">{isAr ? "تفاصيل البنود المخصومة" : "Discounted Line Items"}</h4>
            <p className="text-xs text-stone-500 mt-0.5">
              {isAr ? `${lines.length} بند` : `${lines.length} lines`}
              {(kind || reasonId || staffId) && <span className="ms-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px]">{isAr ? "مُفلتر" : "Filtered"}</span>}
            </p>
          </div>
        </div>
        {lines.length === 0 ? (
          <div className="p-12 text-center text-stone-400 text-sm">
            {isAr ? "لا توجد خصومات في النطاق المحدد." : "No discounts in the selected range."}
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: 480 }}>
            <table className="w-full text-sm" data-testid="discount-lines-table">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 sticky top-0">
                <tr>
                  <th className="text-start py-2 px-3">{isAr ? "التاريخ" : "Date"}</th>
                  <th className="text-start py-2 px-3">{isAr ? "الفاتورة" : "Invoice"}</th>
                  <th className="text-start py-2 px-3">{isAr ? "العميل" : "Customer"}</th>
                  <th className="text-start py-2 px-3">{isAr ? "البند" : "Item"}</th>
                  <th className="text-start py-2 px-3">{isAr ? "الفئة" : "Kind"}</th>
                  <th className="text-end py-2 px-3">{isAr ? "السعر" : "Unit"}</th>
                  <th className="text-end py-2 px-3">{isAr ? "الكمية" : "Qty"}</th>
                  <th className="text-end py-2 px-3">{isAr ? "الخصم" : "Discount"}</th>
                  <th className="text-start py-2 px-3">{isAr ? "السبب" : "Reason"}</th>
                  <th className="text-start py-2 px-3">{isAr ? "الموظف" : "Staff"}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((row, i) => (
                  <tr key={`${row.invoice_id}-${i}`} className="border-t border-stone-100 hover:bg-stone-50/50">
                    <td className="py-2 px-3 text-xs text-stone-600 whitespace-nowrap">
                      {row.created_at ? new Date(row.created_at).toLocaleDateString(isAr ? "ar-EG" : "en-GB", { year: "numeric", month: "short", day: "2-digit" }) : "—"}
                    </td>
                    <td className="py-2 px-3 font-mono text-xs text-stone-700">{row.invoice_number}</td>
                    <td className="py-2 px-3 text-stone-700">{row.customer_name || "—"}</td>
                    <td className="py-2 px-3 text-stone-800 font-medium">{row.item_name}</td>
                    <td className="py-2 px-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{kindLabel(row.kind, isAr)}</span>
                    </td>
                    <td className="py-2 px-3 num-display text-end text-stone-600">{fmt(row.unit_price)}</td>
                    <td className="py-2 px-3 num-display text-end">{row.qty}</td>
                    <td className="py-2 px-3 num-display text-end font-bold text-rose-600">
                      -{fmt(row.line_discount_amount)}
                      <div className="text-[9px] text-stone-400 mt-0.5">
                        {row.discount_type === "percent" ? `${row.discount_value}%` : `${fmt(row.discount_value)} QAR`}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${row.discount_reason_label && row.discount_reason_label !== "—" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-500"}`}>
                        {row.discount_reason_label || "—"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-stone-600">{row.employee_name || row.employee_user_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Small helpers ──
const KpiCard = ({ icon: Icon, color, label, value, suffix, highlight }) => {
  const cls = {
    rose:    "from-rose-50    to-white border-rose-100    text-rose-700",
    amber:   "from-amber-50   to-white border-amber-100   text-amber-700",
    purple:  "from-purple-50  to-white border-purple-100  text-purple-700",
    blue:    "from-blue-50    to-white border-blue-100    text-blue-700",
    emerald: "from-emerald-50 to-white border-emerald-100 text-emerald-700",
  }[color];
  return (
    <div className={`bg-gradient-to-br ${cls} border rounded-xl p-3 ${highlight ? "ring-2 ring-amber-200" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider opacity-75 truncate">{label}</div>
          <div className="text-xl font-bold mt-1 num-display">
            {value}{suffix && <span className="text-[10px] font-normal opacity-60 ms-1">{suffix}</span>}
          </div>
        </div>
        <Icon size={22} className="opacity-50 shrink-0"/>
      </div>
    </div>
  );
};

const BreakdownPanel = ({ title, icon: Icon, rows, empty, color, isAr }) => (
  <div className="card-surface p-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-amber-600"/>
      <h4 className="font-semibold text-[var(--text)]">{title}</h4>
      <span className="ms-auto text-xs text-stone-500">{rows.length}</span>
    </div>
    {rows.length === 0 ? (
      <div className="py-6 text-center text-xs text-stone-400">{empty}</div>
    ) : (
      <div className="space-y-2">
        {rows.slice(0, 8).map((r) => (
          <div key={r.key} className="text-sm">
            <div className="flex justify-between items-baseline gap-2">
              <span className="truncate text-stone-700 font-medium">{r.label}</span>
              <span className="num-display font-bold text-rose-600 shrink-0">-{Number(r.value).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-[10px] text-stone-500 mt-0.5">
              <span>{r.sublabel}</span>
              <span>{r.pct.toFixed(1)}%</span>
            </div>
            <div className="h-1.5 bg-stone-100 rounded-full mt-1 overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${{
                rose: "from-rose-400 to-rose-600",
                amber: "from-amber-400 to-amber-600",
                purple: "from-purple-400 to-purple-600",
              }[color] || "from-stone-400 to-stone-600"}`} style={{ width: `${Math.min(100, r.pct)}%` }}/>
            </div>
          </div>
        ))}
        {rows.length > 8 && (
          <div className="text-[10px] text-stone-400 text-center pt-1">
            {isAr ? `+ ${rows.length - 8} عناصر إضافية` : `+ ${rows.length - 8} more`}
          </div>
        )}
      </div>
    )}
  </div>
);
